/**
 * ISOLATED Hybrid Test Page — PDF View + TipTap Edit + Comment Mode
 * Route: /test/tiptap-editor
 *
 * READ MODE    → Pixel-perfect PDF via PDF.js
 * EDIT MODE    → TipTap rich-text editor
 * COMMENT MODE → TipTap editor with text-select commenting + sidebar (Word/Docs style)
 *
 * TEMPORARY test module — does NOT affect any existing documents or editors
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Mark, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Minus,
  Undo2, Redo2, Code, RemoveFormatting,
  Eye, Edit, ArrowLeft, TableIcon,
  MessageSquarePlus, Send, X,
  Reply, Check, Trash2, Loader2, MessageCircle,
  FileText, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TEST_PDF_HTML } from '@/constants/testPdfHtml';
import { cn } from '@/lib/utils';

/* ═══════════════════════════════════════════════════════
   1. Custom TipTap Mark — CommentMark
   ═══════════════════════════════════════════════════════ */

const CommentMark = Mark.create({
  name: 'commentMark',

  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: el => el.getAttribute('data-comment-id'),
        renderHTML: attrs => {
          if (!attrs.commentId) return {};
          return { 'data-comment-id': attrs.commentId };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-comment-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'comment-highlight' }), 0];
  },

  addProseMirrorPlugins() {
    const markType = this.type;
    return [
      new Plugin({
        key: new PluginKey('commentMarkClick'),
        props: {
          handleClick(view, pos) {
            const resolved = view.state.doc.resolve(pos);
            const marks = resolved.marks();
            const commentMark = marks.find(m => m.type === markType);
            if (commentMark) {
              const commentId = commentMark.attrs.commentId;
              const event = new CustomEvent('comment-mark-click', { detail: { commentId } });
              window.dispatchEvent(event);
            }
            return false;
          },
        },
      }),
    ];
  },
});

/* ═══════════════════════════════════════════════════════
   2. Mock Users for comment authoring
   ═══════════════════════════════════════════════════════ */

const MOCK_COMMENT_USERS = [
  { id: 'u1', name: 'Emily Chen', role: 'Author', color: 'bg-blue-600' },
  { id: 'u2', name: 'Dr. James Wilson', role: 'Reviewer', color: 'bg-emerald-600' },
  { id: 'u3', name: 'Sarah Kim', role: 'Admin', color: 'bg-violet-600' },
];

/* ═══════════════════════════════════════════════════════
   3. Paginated PDF Viewer (Read Mode) — on-demand page loading
   ═══════════════════════════════════════════════════════ */

function PdfViewer() {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jumpInput, setJumpInput] = useState('');

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/pages`)
      .then(r => r.json())
      .then(data => setTotalPages(data.totalPages))
      .catch(err => setError(err.message));
  }, []);

  useEffect(() => {
    if (!totalPages) return;
    setLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/page/${currentPage}`)
      .then(r => { if (!r.ok) throw new Error(`Page ${currentPage} not found`); return r.json(); })
      .then(data => { setPageData(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [currentPage, totalPages]);

  const goTo = useCallback((p) => setCurrentPage(Math.max(1, Math.min(totalPages, p))), [totalPages]);
  const handleJump = useCallback((e) => { e.preventDefault(); const n = parseInt(jumpInput, 10); if (!isNaN(n)) { goTo(n); setJumpInput(''); } }, [jumpInput, goTo]);

  return (
    <div data-testid="pdf-viewer">
      {/* Pagination bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30 sticky top-0 z-10">
        <Button variant="ghost" size="sm" className="h-7 gap-1" disabled={currentPage <= 1 || loading} onClick={() => goTo(currentPage - 1)} data-testid="pdf-prev-btn">
          <ChevronLeft className="h-4 w-4" /> Prev
        </Button>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Page</span>
          <form onSubmit={handleJump} className="inline">
            <Input value={jumpInput || currentPage} onChange={e => setJumpInput(e.target.value)} onFocus={() => setJumpInput(String(currentPage))} onBlur={() => setJumpInput('')}
              className="h-6 w-14 text-center text-xs" data-testid="pdf-page-input" />
          </form>
          <span className="text-muted-foreground">of {totalPages.toLocaleString()}</span>
        </div>
        <Button variant="ghost" size="sm" className="h-7 gap-1" disabled={currentPage >= totalPages || loading} onClick={() => goTo(currentPage + 1)} data-testid="pdf-next-btn">
          Next <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Page content */}
      {loading && (
        <div className="flex items-center justify-center py-24 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" /><p className="text-sm text-muted-foreground">Loading page {currentPage}...</p>
        </div>
      )}
      {error && !pageData && <div className="flex items-center justify-center py-24 text-destructive"><p className="text-sm">{error}</p></div>}
      {!loading && pageData && (
        <div className="py-6 px-4 bg-muted/20">
          <div className="max-w-3xl mx-auto bg-white shadow-lg border rounded p-8 min-h-[60vh]">
            {pageData.header && <div className="doc-header-content text-xs text-muted-foreground border-b pb-3 mb-4" dangerouslySetInnerHTML={{ __html: pageData.header }} />}
            <div className="tiptap-comment-content" data-testid="pdf-page-content">
              <div className="ProseMirror" dangerouslySetInnerHTML={{ __html: pageData.content }} />
            </div>
            {pageData.footer && <div className="doc-footer-content text-xs text-muted-foreground border-t pt-3 mt-4" dangerouslySetInnerHTML={{ __html: pageData.footer }} />}
          </div>
        </div>
      )}

      {/* Bottom pagination */}
      {!loading && pageData && (
        <div className="flex items-center justify-between px-4 py-2 border-t bg-muted/30">
          <Button variant="ghost" size="sm" className="h-7 gap-1" disabled={currentPage <= 1} onClick={() => goTo(currentPage - 1)}>
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <span className="text-xs text-muted-foreground">Page {currentPage} of {totalPages.toLocaleString()}</span>
          <Button variant="ghost" size="sm" className="h-7 gap-1" disabled={currentPage >= totalPages} onClick={() => goTo(currentPage + 1)}>
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   4. Toolbar (shared by Edit & Comment modes)
   ═══════════════════════════════════════════════════════ */

function ToolbarBtn({ icon: Icon, isActive, onClick, disabled, title, className: cls }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      className={cn('p-1.5 rounded-md transition-colors disabled:opacity-30',
        isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-foreground/70 hover:text-foreground', cls)}>
      <Icon className="h-4 w-4" />
    </button>
  );
}

function EditorToolbar({ editor }) {
  if (!editor) return null;
  return (
    <div className="flex flex-wrap items-center gap-0.5 p-2 border-b bg-muted/30 sticky top-0 z-10" data-testid="editor-toolbar">
      <ToolbarBtn icon={Bold} isActive={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold" />
      <ToolbarBtn icon={Italic} isActive={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic" />
      <ToolbarBtn icon={UnderlineIcon} isActive={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline" />
      <ToolbarBtn icon={Strikethrough} isActive={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strike" />
      <ToolbarBtn icon={Code} isActive={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} title="Code" />
      <div className="w-px h-5 bg-border mx-1" />
      <ToolbarBtn icon={Heading1} isActive={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="H1" />
      <ToolbarBtn icon={Heading2} isActive={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="H2" />
      <ToolbarBtn icon={Heading3} isActive={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="H3" />
      <div className="w-px h-5 bg-border mx-1" />
      <ToolbarBtn icon={List} isActive={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet List" />
      <ToolbarBtn icon={ListOrdered} isActive={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Ordered List" />
      <ToolbarBtn icon={Quote} isActive={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote" />
      <ToolbarBtn icon={Minus} onClick={() => editor.chain().focus().setHorizontalRule().run()} title="HR" />
      <ToolbarBtn icon={TableIcon} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Table" />
      <div className="w-px h-5 bg-border mx-1" />
      <ToolbarBtn icon={RemoveFormatting} onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Clear" />
      <ToolbarBtn icon={Undo2} onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" />
      <ToolbarBtn icon={Redo2} onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   5. Comment Sidebar
   ═══════════════════════════════════════════════════════ */

function formatTimestamp(ts) {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function UserAvatar({ user, size = 'md' }) {
  const sz = size === 'sm' ? 'w-5 h-5 text-[9px]' : 'w-7 h-7 text-[11px]';
  return (
    <div className={cn(sz, 'rounded-full flex items-center justify-center font-bold text-white shrink-0', user.color)}
      title={user.name} data-testid={`avatar-${user.id}`}>
      {user.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
    </div>
  );
}

function CommentSidebar({ comments, activeCommentId, onSetActive, onReply, onResolve, onDelete, currentUser }) {
  const [replyTarget, setReplyTarget] = useState(null);
  const [replyText, setReplyText] = useState('');
  const activeRef = useRef(null);

  useEffect(() => {
    if (activeCommentId && activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeCommentId]);

  const handleReply = (commentId) => {
    if (!replyText.trim()) return;
    onReply(commentId, replyText.trim());
    setReplyText('');
    setReplyTarget(null);
  };

  const sortedComments = useMemo(() =>
    [...comments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [comments]
  );

  return (
    <div className="flex flex-col h-full border-l bg-card" data-testid="comment-sidebar">
      <div className="px-3 py-2.5 border-b flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">Comments</span>
          {comments.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">{comments.length}</Badge>
          )}
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-2">
          {sortedComments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <MessageSquarePlus className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-xs">Select text and click</p>
              <p className="text-xs font-medium">Add Comment</p>
            </div>
          ) : (
            sortedComments.map(comment => {
              const isActive = comment.id === activeCommentId;
              return (
                <div
                  key={comment.id}
                  ref={isActive ? activeRef : null}
                  onClick={() => onSetActive(comment.id)}
                  className={cn(
                    'rounded-lg border p-2.5 cursor-pointer transition-all text-sm',
                    isActive ? 'border-primary/50 bg-primary/5 shadow-sm ring-1 ring-primary/20' : 'hover:border-muted-foreground/20 hover:bg-muted/30',
                    comment.resolved && 'opacity-60'
                  )}
                  data-testid={`comment-card-${comment.id}`}
                >
                  {/* Quoted text */}
                  <div className="text-[11px] text-muted-foreground italic bg-muted/50 rounded px-2 py-1 mb-2 line-clamp-2 border-l-2 border-amber-400">
                    "{comment.selectedText}"
                  </div>

                  {/* Author + timestamp */}
                  <div className="flex items-center gap-2 mb-1.5">
                    <UserAvatar user={comment.author} size="sm" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-semibold block truncate">{comment.author.name}</span>
                      <span className="text-[10px] text-muted-foreground">{comment.author.role} · {formatTimestamp(comment.createdAt)}</span>
                    </div>
                    {!comment.resolved && (
                      <button onClick={(e) => { e.stopPropagation(); onResolve(comment.id); }}
                        className="text-[10px] text-green-600 hover:underline flex items-center gap-0.5 shrink-0" title="Resolve">
                        <Check className="h-3 w-3" /> Resolve
                      </button>
                    )}
                    {comment.resolved && (
                      <Badge variant="outline" className="text-[10px] text-green-600 border-green-200 shrink-0">
                        <Check className="h-3 w-3 mr-0.5" /> Resolved
                      </Badge>
                    )}
                  </div>

                  {/* Comment text */}
                  <p className="text-xs leading-relaxed mb-2">{comment.text}</p>

                  {/* Replies */}
                  {comment.replies.length > 0 && (
                    <div className="pl-3 border-l-2 border-muted space-y-2 mb-2">
                      {comment.replies.map(reply => (
                        <div key={reply.id} className="text-xs" data-testid={`reply-${reply.id}`}>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <UserAvatar user={reply.author} size="sm" />
                            <span className="font-medium">{reply.author.name}</span>
                            <span className="text-[10px] text-muted-foreground ml-auto">{formatTimestamp(reply.createdAt)}</span>
                          </div>
                          <p className="pl-6 leading-relaxed">{reply.text}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply input */}
                  {replyTarget === comment.id ? (
                    <div className="flex gap-1.5 mt-1" onClick={e => e.stopPropagation()}>
                      <Input autoFocus value={replyText} onChange={e => setReplyText(e.target.value)}
                        placeholder="Write a reply..." className="h-7 text-xs"
                        onKeyDown={e => e.key === 'Enter' && handleReply(comment.id)}
                        data-testid={`reply-input-${comment.id}`} />
                      <Button size="sm" className="h-7 px-2" onClick={() => handleReply(comment.id)} disabled={!replyText.trim()}>
                        <Send className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-1.5" onClick={() => { setReplyTarget(null); setReplyText(''); }}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <button onClick={(e) => { e.stopPropagation(); setReplyTarget(comment.id); }}
                        className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5">
                        <Reply className="h-3 w-3" /> Reply
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); onDelete(comment.id); }}
                        className="text-[10px] text-muted-foreground hover:text-destructive flex items-center gap-0.5 ml-auto">
                        <Trash2 className="h-3 w-3" /> Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   6. Comment Editor Mode (TipTap + Sidebar)
   ═══════════════════════════════════════════════════════ */

function CommentEditorMode() {
  const [comments, setComments] = useState([]);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [currentUserIdx, setCurrentUserIdx] = useState(0);
  const [selectionBubble, setSelectionBubble] = useState(null); // { from, to, selectedText, top, right }
  const [commentPopup, setCommentPopup] = useState(null); // { from, to, selectedText, top, right }
  const [commentText, setCommentText] = useState('');
  const editorWrapperRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const currentUser = MOCK_COMMENT_USERS[currentUserIdx];

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Image.configure({ inline: false, allowBase64: false }),
      Table.configure({ resizable: true }),
      TableRow, TableCell, TableHeader,
      CommentMark,
    ],
    content: TEST_PDF_HTML,
    editable: true,
    onSelectionUpdate: ({ editor: ed }) => {
      // Auto-show floating button when text is selected
      if (commentPopup) return; // don't override open popup
      const { from, to } = ed.state.selection;
      if (from === to) {
        setSelectionBubble(null);
        return;
      }
      const selectedText = ed.state.doc.textBetween(from, to, ' ');
      if (!selectedText.trim()) { setSelectionBubble(null); return; }

      const scrollEl = scrollContainerRef.current;
      const wrapperEl = editorWrapperRef.current;
      if (!scrollEl || !wrapperEl) return;

      const coords = ed.view.coordsAtPos(from);
      const endCoords = ed.view.coordsAtPos(to);
      const scrollRect = scrollEl.getBoundingClientRect();

      // Position: to the right of the selection, vertically centered
      const topInScroll = ((coords.top + endCoords.bottom) / 2) - scrollRect.top + scrollEl.scrollTop - 16;
      setSelectionBubble({ from, to, selectedText, top: topInScroll });
    },
  });

  // Listen for comment mark clicks from the ProseMirror plugin
  useEffect(() => {
    const handler = (e) => setActiveCommentId(e.detail.commentId);
    window.addEventListener('comment-mark-click', handler);
    return () => window.removeEventListener('comment-mark-click', handler);
  }, []);

  // Scroll to highlighted text when active comment changes
  useEffect(() => {
    if (!activeCommentId || !editorWrapperRef.current) return;
    const el = editorWrapperRef.current.querySelector(`[data-comment-id="${activeCommentId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('comment-flash');
      setTimeout(() => el.classList.remove('comment-flash'), 1200);
    }
  }, [activeCommentId]);

  // Open comment input popup from the floating button
  const openCommentPopup = useCallback(() => {
    if (!selectionBubble) return;
    setCommentPopup({ ...selectionBubble });
    setSelectionBubble(null);
    setCommentText('');
  }, [selectionBubble]);

  const cancelPopup = useCallback(() => {
    setCommentPopup(null);
    setCommentText('');
  }, []);

  const submitComment = useCallback(() => {
    if (!commentPopup || !commentText.trim() || !editor) return;
    const commentId = `cmt-${Date.now()}`;

    editor.chain().focus()
      .setTextSelection({ from: commentPopup.from, to: commentPopup.to })
      .setMark('commentMark', { commentId })
      .run();

    setComments(prev => [...prev, {
      id: commentId,
      selectedText: commentPopup.selectedText,
      text: commentText.trim(),
      author: currentUser,
      createdAt: new Date().toISOString(),
      resolved: false,
      replies: [],
    }]);

    setActiveCommentId(commentId);
    setCommentPopup(null);
    setCommentText('');
  }, [commentPopup, commentText, editor, currentUser]);

  const handleReply = useCallback((commentId, replyText) => {
    setComments(prev => prev.map(c =>
      c.id === commentId ? {
        ...c,
        replies: [...c.replies, {
          id: `rpl-${Date.now()}`,
          text: replyText,
          author: currentUser,
          createdAt: new Date().toISOString(),
        }],
      } : c
    ));
  }, [currentUser]);

  const handleResolve = useCallback((commentId) => {
    setComments(prev => prev.map(c =>
      c.id === commentId ? { ...c, resolved: true } : c
    ));
  }, []);

  const handleDelete = useCallback((commentId) => {
    if (editor) {
      const { doc } = editor.state;
      doc.descendants((node, pos) => {
        node.marks.forEach(mark => {
          if (mark.type.name === 'commentMark' && mark.attrs.commentId === commentId) {
            editor.chain()
              .setTextSelection({ from: pos, to: pos + node.nodeSize })
              .unsetMark('commentMark')
              .run();
          }
        });
      });
    }
    setComments(prev => prev.filter(c => c.id !== commentId));
    if (activeCommentId === commentId) setActiveCommentId(null);
  }, [editor, activeCommentId]);

  return (
    <div className="flex h-[calc(100vh-52px)]" data-testid="comment-editor-mode">
      {/* Editor area */}
      <div className="flex-1 flex flex-col overflow-hidden" ref={editorWrapperRef}>
        <EditorToolbar editor={editor} />

        {/* User switcher bar */}
        <div className="flex items-center gap-2 px-3 py-1.5 border-b bg-background text-xs shrink-0">
          <span className="text-muted-foreground">Commenting as:</span>
          {MOCK_COMMENT_USERS.map((u, idx) => (
            <button key={u.id} onClick={() => setCurrentUserIdx(idx)}
              className={cn('flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors',
                idx === currentUserIdx ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted text-muted-foreground')}
              data-testid={`user-switch-${u.id}`}>
              <UserAvatar user={u} size="sm" />
              {u.name}
            </button>
          ))}
        </div>

        {/* Editor content with floating UI */}
        <div className="flex-1 overflow-auto relative" ref={scrollContainerRef}>
          <div className="tiptap-comment-content">
            {editor && <EditorContent editor={editor} />}
          </div>

          {/* Floating "Add Comment" button — appears on text selection */}
          {selectionBubble && !commentPopup && (
            <button
              className="absolute z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium shadow-lg transition-all animate-in fade-in zoom-in-95 duration-150"
              style={{ top: selectionBubble.top, right: 16 }}
              onMouseDown={e => e.preventDefault()}
              onClick={openCommentPopup}
              data-testid="floating-add-comment-btn"
            >
              <MessageSquarePlus className="h-3.5 w-3.5" />
              Comment
            </button>
          )}

          {/* Comment input popup */}
          {commentPopup && (
            <div className="absolute z-30 w-80 bg-card border rounded-lg shadow-2xl p-3 space-y-2.5 animate-in fade-in slide-in-from-right-2 duration-150"
              style={{ top: commentPopup.top, right: 16 }}
              onMouseDown={e => e.stopPropagation()}
              data-testid="comment-popup">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <UserAvatar user={currentUser} size="sm" />
                  <span className="text-xs font-medium">{currentUser.name}</span>
                  <Badge variant="secondary" className="text-[9px] h-4 px-1">{currentUser.role}</Badge>
                </div>
                <button onClick={cancelPopup} className="text-muted-foreground hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="text-[11px] text-muted-foreground italic bg-muted/50 rounded px-2 py-1 border-l-2 border-amber-400 line-clamp-2">
                "{commentPopup.selectedText}"
              </div>
              <div className="flex gap-1.5">
                <Input autoFocus value={commentText} onChange={e => setCommentText(e.target.value)}
                  placeholder="Add a comment..." className="h-8 text-xs"
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                  data-testid="comment-popup-input" />
                <Button size="sm" className="h-8 px-2.5" onClick={submitComment} disabled={!commentText.trim()}
                  data-testid="comment-popup-submit">
                  <Send className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Comment Sidebar */}
      <div className="w-80 shrink-0 overflow-hidden">
        <CommentSidebar
          comments={comments}
          activeCommentId={activeCommentId}
          onSetActive={setActiveCommentId}
          onReply={handleReply}
          onResolve={handleResolve}
          onDelete={handleDelete}
          currentUser={currentUser}
        />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   7. Paginated Edit Mode (TipTap + PDF API)
   ═══════════════════════════════════════════════════════ */

function EditOnlyMode() {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jumpInput, setJumpInput] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5, 6] } }),
      Underline,
      Image.configure({ inline: false, allowBase64: false }),
      Table.configure({ resizable: true }),
      TableRow, TableCell, TableHeader,
    ],
    content: '<p>Loading...</p>',
    editable: true,
  });

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/pages`)
      .then(r => r.json())
      .then(data => setTotalPages(data.totalPages))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!totalPages) return;
    setLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/page/${currentPage}`)
      .then(r => r.json())
      .then(data => {
        setPageData(data);
        if (editor) editor.commands.setContent(data.content || '<p></p>');
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [currentPage, totalPages, editor]);

  const goTo = useCallback((p) => { setCurrentPage(Math.max(1, Math.min(totalPages, p))); setJumpInput(''); }, [totalPages]);
  const handleJump = useCallback((e) => { e.preventDefault(); const n = parseInt(jumpInput, 10); if (!isNaN(n)) goTo(n); }, [jumpInput, goTo]);

  return (
    <div className="max-w-5xl mx-auto my-6" data-testid="edit-only-mode">
      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-2 mb-3 bg-card border rounded-lg shadow-sm" data-testid="edit-pagination-bar">
        <Button variant="outline" size="sm" className="h-8 gap-1.5" disabled={currentPage <= 1 || loading} onClick={() => goTo(currentPage - 1)} data-testid="edit-prev-btn">
          <ChevronLeft className="h-4 w-4" /> Previous
        </Button>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Page</span>
          <form onSubmit={handleJump} className="inline">
            <Input value={jumpInput || currentPage} onChange={e => setJumpInput(e.target.value)} onFocus={() => setJumpInput(String(currentPage))} onBlur={() => setJumpInput('')}
              className="h-7 w-16 text-center text-sm" data-testid="edit-page-input" />
          </form>
          <span className="text-muted-foreground">of {totalPages.toLocaleString()}</span>
        </div>
        <Button variant="outline" size="sm" className="h-8 gap-1.5" disabled={currentPage >= totalPages || loading} onClick={() => goTo(currentPage + 1)} data-testid="edit-next-btn">
          Next <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Editor card */}
      <div className="border rounded-lg bg-card shadow-sm overflow-hidden">
        {pageData?.header && (
          <div className="px-8 py-4 bg-muted/40 border-b" data-testid="edit-page-header">
            <div className="doc-header-content text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: pageData.header }} />
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Loading page {currentPage}...</span>
          </div>
        ) : (
          <>
            <EditorToolbar editor={editor} />
            <div className="tiptap-comment-content">
              {editor && <EditorContent editor={editor} />}
            </div>
          </>
        )}

        {pageData?.footer && (
          <div className="px-8 py-3 bg-muted/40 border-t" data-testid="edit-page-footer">
            <div className="doc-footer-content text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: pageData.footer }} />
          </div>
        )}
      </div>

      {/* Bottom pagination */}
      <div className="flex items-center justify-between px-4 py-2 mt-3 bg-card border rounded-lg shadow-sm">
        <Button variant="outline" size="sm" className="h-8 gap-1.5" disabled={currentPage <= 1 || loading} onClick={() => goTo(currentPage - 1)}>
          <ChevronLeft className="h-4 w-4" /> Previous
        </Button>
        <span className="text-xs text-muted-foreground">Page {currentPage} of {totalPages.toLocaleString()}</span>
        <Button variant="outline" size="sm" className="h-8 gap-1.5" disabled={currentPage >= totalPages || loading} onClick={() => goTo(currentPage + 1)}>
          Next <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   7b. Document Viewer Mode (Paginated TipTap — Phase 2)
   ═══════════════════════════════════════════════════════ */

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function DocViewerMode() {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jumpInput, setJumpInput] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5, 6] } }),
      Underline,
      Image.configure({ inline: false, allowBase64: false }),
      Table.configure({ resizable: true }),
      TableRow, TableCell, TableHeader,
    ],
    content: '<p>Loading document...</p>',
    editable: true,
  });

  // Fetch total pages on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/documents/test-doc/pages`)
      .then(r => r.json())
      .then(data => setTotalPages(data.totalPages))
      .catch(err => setError(err.message));
  }, []);

  // Fetch page content when currentPage changes
  useEffect(() => {
    if (!totalPages) return;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/api/documents/test-doc/page/${currentPage}`)
      .then(r => {
        if (!r.ok) throw new Error(`Page ${currentPage} not found`);
        return r.json();
      })
      .then(data => {
        setPageData(data);
        if (editor) {
          editor.commands.setContent(data.content || '<p></p>');
        }
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [currentPage, totalPages, editor]);

  const goTo = useCallback((page) => {
    const p = Math.max(1, Math.min(totalPages, page));
    setCurrentPage(p);
    setJumpInput('');
  }, [totalPages]);

  const handleJump = useCallback((e) => {
    e.preventDefault();
    const n = parseInt(jumpInput, 10);
    if (!isNaN(n)) goTo(n);
  }, [jumpInput, goTo]);

  if (error && !pageData) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-destructive" data-testid="doc-viewer-error">
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto my-6" data-testid="doc-viewer-mode">
      {/* Pagination bar */}
      <div className="flex items-center justify-between px-4 py-2 mb-3 bg-card border rounded-lg shadow-sm" data-testid="doc-pagination-bar">
        <Button variant="outline" size="sm" className="h-8 gap-1.5"
          disabled={currentPage <= 1 || loading}
          onClick={() => goTo(currentPage - 1)}
          data-testid="doc-prev-btn">
          <ChevronLeft className="h-4 w-4" /> Previous
        </Button>

        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Page</span>
          <form onSubmit={handleJump} className="inline">
            <Input
              value={jumpInput || currentPage}
              onChange={e => setJumpInput(e.target.value)}
              onFocus={() => setJumpInput(String(currentPage))}
              onBlur={() => setJumpInput('')}
              className="h-7 w-16 text-center text-sm"
              data-testid="doc-page-input"
            />
          </form>
          <span className="text-muted-foreground">of {totalPages.toLocaleString()}</span>
        </div>

        <Button variant="outline" size="sm" className="h-8 gap-1.5"
          disabled={currentPage >= totalPages || loading}
          onClick={() => goTo(currentPage + 1)}
          data-testid="doc-next-btn">
          Next <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Document page card */}
      <div className="border rounded-lg bg-card shadow-sm overflow-hidden">
        {/* Header section */}
        {pageData?.header && (
          <div className="px-8 py-4 bg-muted/40 border-b" data-testid="doc-page-header">
            <div className="doc-header-content text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: pageData.header }} />
          </div>
        )}

        {/* Loading overlay */}
        {loading && (
          <div className="flex items-center justify-center py-16 gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Loading page {currentPage}...</span>
          </div>
        )}

        {/* TipTap editor content */}
        {!loading && (
          <>
            <EditorToolbar editor={editor} />
            <div className="tiptap-comment-content">
              {editor && <EditorContent editor={editor} />}
            </div>
          </>
        )}

        {/* Footer section */}
        {pageData?.footer && (
          <div className="px-8 py-3 bg-muted/40 border-t" data-testid="doc-page-footer">
            <div className="doc-footer-content text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: pageData.footer }} />
          </div>
        )}
      </div>

      {/* Bottom pagination */}
      <div className="flex items-center justify-between px-4 py-2 mt-3 bg-card border rounded-lg shadow-sm">
        <Button variant="outline" size="sm" className="h-8 gap-1.5"
          disabled={currentPage <= 1 || loading}
          onClick={() => goTo(currentPage - 1)}
          data-testid="doc-prev-btn-bottom">
          <ChevronLeft className="h-4 w-4" /> Previous
        </Button>
        <span className="text-xs text-muted-foreground">
          Page {currentPage} of {totalPages.toLocaleString()}
        </span>
        <Button variant="outline" size="sm" className="h-8 gap-1.5"
          disabled={currentPage >= totalPages || loading}
          onClick={() => goTo(currentPage + 1)}
          data-testid="doc-next-btn-bottom">
          Next <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   8. Main Page — Mode Switcher
   ═══════════════════════════════════════════════════════ */

export default function TipTapTestPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('document'); // 'read' | 'edit' | 'comment' | 'document'

  return (
    <div className="min-h-screen bg-background" data-testid="tiptap-test-page">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-card sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)} data-testid="test-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-sm font-semibold">Hybrid Editor Test</h1>
            <p className="text-xs text-muted-foreground">sample-report.pdf</p>
          </div>
          <Badge variant="outline" className="text-[10px] ml-2 border-amber-300 text-amber-600">TEST ONLY</Badge>
        </div>

        <div className="flex items-center gap-0.5 bg-muted rounded-lg p-0.5" data-testid="mode-switcher">
          {[
            { key: 'document', icon: FileText, label: 'Document' },
            { key: 'read', icon: Eye, label: 'PDF View' },
            { key: 'edit', icon: Edit, label: 'Edit' },
            { key: 'comment', icon: MessageCircle, label: 'Comment' },
          ].map(({ key, icon: Icon, label }) => (
            <button key={key} onClick={() => setMode(key)}
              className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                mode === key ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground')}
              data-testid={`${key}-mode-btn`}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {mode === 'document' && <DocViewerMode />}
      {mode === 'read' && (
        <div className="max-w-5xl mx-auto my-6 border rounded-lg bg-card shadow-sm overflow-hidden">
          <PdfViewer />
        </div>
      )}
      {mode === 'edit' && <EditOnlyMode />}
      {mode === 'comment' && <CommentEditorMode />}

      {/* Styles scoped to this test page */}
      <style>{`
        .tiptap-comment-content .ProseMirror {
          padding: 2rem 3rem;
          min-height: 60vh;
          outline: none;
          font-size: 0.9375rem;
          line-height: 1.7;
          color: hsl(var(--foreground, 222.2 84% 4.9%));
        }
        .tiptap-comment-content .ProseMirror h1 {
          font-size: 1.75rem; font-weight: 700;
          margin-top: 2rem; margin-bottom: 0.75rem;
          line-height: 1.3; color: hsl(221, 83%, 53%);
        }
        .tiptap-comment-content .ProseMirror h2 {
          font-size: 1.35rem; font-weight: 600;
          margin-top: 1.75rem; margin-bottom: 0.5rem;
          line-height: 1.35; color: hsl(221, 83%, 53%);
        }
        .tiptap-comment-content .ProseMirror h3 {
          font-size: 1.1rem; font-weight: 600;
          margin-top: 1.25rem; margin-bottom: 0.5rem; line-height: 1.4;
        }
        .tiptap-comment-content .ProseMirror p { margin-bottom: 0.75rem; }
        .tiptap-comment-content .ProseMirror ul,
        .tiptap-comment-content .ProseMirror ol { padding-left: 1.5rem; margin-bottom: 0.75rem; }
        .tiptap-comment-content .ProseMirror li { margin-bottom: 0.25rem; }
        .tiptap-comment-content .ProseMirror blockquote {
          border-left: 3px solid hsl(221, 83%, 53%);
          padding-left: 1rem; margin: 1rem 0;
          color: hsl(var(--muted-foreground, 215.4 16.3% 46.9%)); font-style: italic;
        }
        .tiptap-comment-content .ProseMirror hr {
          border: none; border-top: 2px solid hsl(var(--border, 214.3 31.8% 91.4%)); margin: 2rem 0;
        }
        .tiptap-comment-content .ProseMirror code {
          background: hsl(var(--muted, 210 40% 96.1%));
          border-radius: 0.25rem; padding: 0.15rem 0.4rem; font-size: 0.875em;
        }
        .tiptap-comment-content .ProseMirror img {
          max-width: 100%; height: auto;
          border-radius: 0.5rem; border: 1px solid hsl(var(--border, 214.3 31.8% 91.4%)); margin: 1rem 0;
        }
        .tiptap-comment-content .ProseMirror table {
          border-collapse: collapse; width: 100%; margin: 1rem 0;
          border: 1px solid hsl(var(--border, 214.3 31.8% 91.4%));
        }
        .tiptap-comment-content .ProseMirror th,
        .tiptap-comment-content .ProseMirror td {
          border: 1px solid hsl(var(--border, 214.3 31.8% 91.4%));
          padding: 0.5rem 0.75rem; text-align: left; font-size: 0.875rem; min-width: 80px;
        }
        .tiptap-comment-content .ProseMirror th {
          background: hsl(var(--muted, 210 40% 96.1%)); font-weight: 600;
        }
        .tiptap-comment-content .ProseMirror tr:hover td {
          background: hsl(var(--muted, 210 40% 96.1%) / 0.5);
        }
        .tiptap-comment-content .ProseMirror .selectedCell {
          background: hsl(221 83% 53% / 0.1);
        }
        /* Comment highlight */
        .comment-highlight {
          background-color: rgba(255, 213, 79, 0.3);
          border-bottom: 2px solid #ffa726;
          cursor: pointer;
          padding: 0 1px;
          transition: background-color 0.2s;
        }
        .comment-highlight:hover {
          background-color: rgba(255, 183, 77, 0.5);
        }
        /* Flash when scrolled to */
        @keyframes commentFlash {
          0%, 100% { background-color: rgba(255, 213, 79, 0.3); }
          50% { background-color: rgba(255, 152, 0, 0.5); }
        }
        .comment-flash {
          animation: commentFlash 0.4s ease-in-out 3;
        }
        /* Doc viewer header/footer tables */
        .doc-header-content table,
        .doc-footer-content table {
          border-collapse: collapse; width: 100%;
          border: 1px solid hsl(var(--border, 214.3 31.8% 91.4%));
        }
        .doc-header-content th,
        .doc-header-content td,
        .doc-footer-content th,
        .doc-footer-content td {
          border: 1px solid hsl(var(--border, 214.3 31.8% 91.4%));
          padding: 0.35rem 0.5rem; text-align: left; font-size: 0.75rem;
        }
        .doc-header-content th,
        .doc-footer-content th {
          background: hsl(var(--muted, 210 40% 96.1%)); font-weight: 600;
        }
        .doc-header-content p,
        .doc-footer-content p { margin-bottom: 0.25rem; }
      `}</style>
    </div>
  );
}
